<script setup>
    import { ref, watch } from 'vue'
    import { NavigationControls } from '../stores/NavigationControls'
    import NavigationToggle from './NavigationToggle'

    const props = defineProps({
        menu: Object,

        links: Object,

        featuredtitle: {
            type: String,
            required: false,
            default: () => { return null }
        },

        featuredimage: {
            type: [String, Object],
            required: false,
            default: () => { return null }
        },

        featuredintro: {
            type: String,
            required: false,
            default: () => { return null }
        },

        featuredlink: {
            type: String,
            required: false,
            default: () => { return null }
        }
    })
    const navigationControls = NavigationControls()

    const subMenuOpen = ref(-1),
          subChildMenuOpen = ref(-1)

    function subMenuToggle(value) {
        if (subMenuOpen.value === value) {
            subMenuOpen.value = -1
            subChildMenuOpen.value = -1
        } else {
            subMenuOpen.value = value
        }
    }

    function subChildMenuToggle(value) {
        if (subChildMenuOpen.value === value) {
            subChildMenuOpen.value = -1
        } else {
            subChildMenuOpen.value = value
        }
    }

    watch(() => navigationControls.visible, (currentValue) => {
        if (currentValue) {
            document.documentElement.classList.add('scroll-lock')
        } else {
            document.documentElement.classList.remove('scroll-lock')
            subMenuOpen.value = -1
            subChildMenuOpen.value = -1
        }
    })
</script>
<template>
    <transition name="menu-toggle">
        <div
            v-show="navigationControls.visible"
            class="w-screen h-[calc(theme(height.screen)_-_theme(space.32))] bg-cintra-grey dark fixed inset-0 z-10 top-32"
        >
            <div class="absolute w-full top-0 left-0 h-12 bg-gradient-to-b from-cintra-grey to-transparent z-1 pointer-events-none" aria-hidden="true" />
            <div class="absolute w-full bottom-0 left-0 h-12 bg-gradient-to-t from-cintra-grey to-transparent z-1 pointer-events-none" aria-hidden="true" />
            <div class="block-inner h-full grid md:grid-cols-5 py-4 md:py-12 relative overflow-y-auto">
                <article
                    v-if="props.featuredtitle"
                    class="dark md:col-span-2 w-full hidden md:block pr-14 lg:pr-24"
                >
                    <a
                        :href="props.featuredlink"
                        :aria-label="props.featuredtitle"
                        class="group"
                    >
                        <div
                            v-if="props.featuredimage"
                            class="aspect-2/1 w-full relative overflow-hidden"
                        >
                            <picture>
                                <source
                                    v-if="props.featuredimage?.webp"
                                    :srcset="props.featuredimage.webp"
                                    :width="props.featuredimage?.width"
                                    :height="props.featuredimage?.height"
                                    :alt="props.featuredtitle"
                                    type="image/webp"
                                    class="absolute top-1/2 left-1/2 -translate-y-1/2 -translate-x-1/2 object-cover transition duration-300 group-hover:scale-110"
                                >
                                <img
                                    :src="props.featuredimage.default"
                                    :width="props.featuredimage?.width"
                                    :height="props.featuredimage?.height"
                                    :alt="props.featuredtitle"
                                    class="absolute top-1/2 left-1/2 -translate-y-1/2 -translate-x-1/2 object-cover transition duration-300 group-hover:scale-110"
                                >
                            </picture>
                        </div>
                    </a>
                    <div class="w-full">
                        <h3 class="text-2xl lg:text-3xl 2xl:text-4xl">{{ props.featuredtitle }}</h3>
                        <p>{{ props.featuredintro }}</p>
                        <a class="arrow-button" :href="props.featuredlink">Find out more</a>
                    </div>
                </article>
                <div class="w-full max-w-2xl md:col-span-3 md:pl-14 lg:pl-24 md:border-l md:border-l-white flex flex-col overflow-hidden pb-24 relative">
                    <ul
                        role="menubar"
                        aria-orientation="vertical"
                        aria-label="Top level navigation menu"
                        class="w-full relative"
                    >
                        <li
                            v-for="(item, menuKey) in props.menu"
                            :key="menuKey"
                            role="none"
                            class="menu-item w-full pb-3 md:pr-12 group"
                        >
                            <div
                                class="w-full flex justify-between transition motion-reduce:transition-none motion-reduce:duration-[0s] duration-500"
                                :class="{
                                    '-translate-x-1/3 opacity-0 pointer-events-none' : subMenuOpen !== -1,
                                    'translate-x-0' : subMenuOpen === -1
                                }">
                                <a
                                    :href="item.url"
                                    role="menuitem"
                                    class="block w-full py-1 2xl:py-2 text-cintra-orange text-xl sm:text-2xl 2xl:text-3xl font-bold transition duration-300 group-hover:text-white"
                                >
                                    {{ item.title }}
                                </a>
                                <button
                                    v-if="item.children.length"
                                    :aria-label='`Open the "${item.title}" submenu`'
                                    aria-haspopup="true"
                                    type="button"
                                    role="menuitem"
                                    class="w-12 transition duration-300 flex items-center justify-center"
                                    @click="subMenuToggle(menuKey)"
                                >
                                    <i class="fa-thin fa-circle-arrow-right text-3xl text-cintra-orange transition duration-300 hover:text-white"></i>
                                </button>
                            </div>
                            <div
                                v-if="item.children.length"
                                :aria-hidden="subMenuOpen !== menuKey ? 'true' : 'false'"
                                :aria-expanded="subMenuOpen === menuKey ? 'true' : 'false'"
                                :aria-label="`${item.title} sublevel navigation menu`"
                                class="absolute top-0 w-full transition motion-reduce:transition-none motion-reduce:duration-[0s] duration-500"
                                :class="{
                                    'opacity-100 visible' : item.children && subMenuOpen === menuKey,
                                    'opacity-0 invisible pointer-events-none' : item.children && subMenuOpen !== menuKey
                                }"
                            >
                                <button
                                    :aria-label='`Close the "${item.title}" submenu`'
                                    type="button"
                                    class="arrow-button arrow-button--left mb-3"
                                    @click="subMenuToggle(menuKey)"
                                >
                                    <span>Main menu</span>
                                </button>
                                <ul
                                    :aria-label='`"${item.title}" sublevel navigation menu`'
                                    role="menu"
                                    aria-orientation="vertical"
                                    class="sub-menu"
                                >
                                    <li
                                        v-for="(childItem, subMenuKey) in item.children"
                                        :key="subMenuKey"
                                        role="none"
                                        class="pb-3"
                                    >
                                        <transition name="sub-menu-toggle">
                                            <div
                                                v-if="item.children.length && subMenuOpen === menuKey"
                                                class="flex"
                                            >
                                                <a
                                                    :href="childItem.url"
                                                    class="block py-1 2xl:py-2 text-cintra-orange text-xl sm:text-2xl 2xl:text-3xl font-bold w-full transition duration-300 hover:text-white"
                                                    role="menuitem"
                                                >
                                                    {{ childItem.title }}
                                                </a>
                                                <button
                                                    v-if="childItem.children.length"
                                                    :aria-expanded="subChildMenuOpen === subMenuKey ? 'true' : 'false'"
                                                    :aria-label='`Toggle the "${childItem.title}" submenu`'
                                                    aria-haspopup="true"
                                                    type="button"
                                                    role="menuitem"
                                                    class="w-12 transition duration-300 flex items-center justify-center"
                                                    :class="{
                                                        'flip-x' : subChildMenuOpen === subMenuKey
                                                    }"
                                                    @click="subChildMenuToggle(subMenuKey)"
                                                >
                                                    <i class="fa-thin fa-circle-arrow-down text-3xl text-cintra-orange transition duration-300 hover:text-white"></i>
                                                </button>
                                            </div>
                                        </transition>
                                        <transition name="sub-child-menu-toggle">
                                            <ul
                                                v-if="childItem.children.length"
                                                v-show="subChildMenuOpen === subMenuKey"
                                                :aria-hidden="subChildMenuOpen !== subMenuKey ? 'true' : 'false'"
                                                :aria-expanded="subChildMenuOpen === subMenuKey ? 'true' : 'false'"
                                                :aria-label='`"${childItem.title}" sublevel navigation menu`'
                                                role="menu"
                                                aria-orientation="vertical"
                                                class="dark:text-cintra-orange pl-8"
                                            >
                                                <li
                                                    v-for="( subChild, subChildMenuKey ) in childItem.children"
                                                    :key="subChildMenuKey"
                                                    role="none"
                                                >
                                                    <a
                                                        :href="subChild.url"
                                                        role="menuitem"
                                                        class="transition duration-300 hover:text-white inline-block w-full"
                                                    >
                                                        {{ subChild.title }}
                                                    </a>
                                                </li>
                                            </ul>
                                        </transition>
                                    </li>
                                </ul>
                            </div>
                        </li>
                    </ul>
                    <div class="w-full absolute bottom-6">
                        <form method="GET" action="/search" class="relative max-w-sm">
                            <label for="search_input" class="sr-only">Input your search term</label>
                            <input id="search_input" name="query" class="global-search py-3 pl-6 pr-12 m-0 w-full inline-block max-w-sm rounded-2xl" type="text" placeholder="Search by keyword">
                            <button type="submit" aria-label="Submit search" class="absolute top-0 right-0 bottom-0 flex items-center pl-2 pr-6">
                                <i class="fa-sharp fa-solid fa-magnifying-glass text-cintra-orange" />
                            </button>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    </transition>
</template>
<style lang="scss" scoped>
    .menu-item-icon-rotate {
        transform: rotateX(180deg) translateY(50%);
    }

    @media screen and (min-width: 768px) {
        .menu-item-icon-rotate {
            transform: rotateY(180deg) translateX(50%);
        }
    }

    .menu-toggle-enter-active,
    .menu-toggle-leave-active {
        @apply transition motion-reduce:transition-none motion-reduce:duration-[0s] duration-500;
    }
    .menu-toggle-enter-from,
    .menu-toggle-enter,
    .menu-toggle-leave-to {
        @apply opacity-0;
    }
    .menu-toggle-enter-to {
        @apply opacity-100;
    }
    .sub-menu-toggle-enter-active,
    .sub-menu-toggle-leave-active,
    .sub-child-menu-toggle-enter-active,
    .sub-child-menu-toggle-leave-active {
        @apply transition motion-reduce:transition-none motion-reduce:duration-[0s] duration-500;
    }
    .sub-menu-toggle-enter-from,
    .sub-menu-toggle-enter,
    .sub-menu-toggle-leave-to,
    .sub-child-menu-toggle-enter-from,
    .sub-child-menu-toggle-enter,
    .sub-child-menu-toggle-leave-to {
        @apply opacity-0;
        @apply translate-x-1/3;
    }
    .sub-menu-toggle-enter-to,
    .sub-child-menu-toggle-enter-to {
        @apply opacity-100;
        @apply translate-x-0;
    }
</style>
